import {
  getRequiredFields,
  removeEmptyFields,
  setEmptyFieldsToNull,
} from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import {
  ACCOUNT_TYPE,
  ADDRESS1,
  ADDRESS2,
  CITY,
  EDUCATOR_TYPE,
  EMAIL,
  FACULTY_DAYS,
  GRADES_SELECTED,
  INCLUDES_BOOKS,
  MAINTENANCE,
  NO_FACULTY_DAYS,
  NO_OF_WORKSTATIONS,
  PHONE,
  REGION,
  SCHOOL_LOGO,
  SCHOOL_NAME,
  SCHOOL_TIER,
  SKU,
  STATE,
  STUDENT_STRENGTH,
  SYLLABUS,
  WEBSITE,
  YEARS_OF_COLLABORATION,
  ZIP,
} from "./consts";
import { formValues, schema } from "./validations";
import { useNavigate } from "react-router-dom";
import { getSchoolRedirectPath } from "@/routes/utils";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { useAppDispatch } from "@/redux/hooks";
import { setSchoolOnboardingId } from "@/redux/slices/currentDataSlice";
import { useEffect, useMemo, useRef } from "react";
import { TabsDataTypes } from "../types";
import {
  useCreateSchoolMutation,
  useGetSchoolByIdQuery,
  useUpdateSchoolMutation,
} from "@/services";
import { useParams } from "react-router-dom";
import { useGetCurriculumGrades } from "@/services/masterData/useGetCurriculumGrades";
import { useGetActiveGeoRegions } from "@/services/masterData/useGetActiveGeoRegions";

const useSchoolInfo = ({ onNextClick }: TabsDataTypes) => {
  const navigate = useNavigate();
  const attachmentRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  
  const loggedInUserData = useSelector(
    (state: RootState) => state.currentData.loggedInUserData
  );
  const initialValuesRef = useRef<Partial<formValues>>({});
  const { id, action } = useParams();
  const form = useForm<formValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      // Basic Info
      [SCHOOL_NAME]: "",
      [STATE]: "",
      [CITY]: "",
      [ZIP]: "",
      [REGION]: "",
      [PHONE]: "",
      [EMAIL]: "",
      [WEBSITE]: "",
      [SCHOOL_LOGO]: null,
      [ADDRESS1]: "",
      [ADDRESS2]: "",

      // Academic and operational info
      [YEARS_OF_COLLABORATION]: "",
      [GRADES_SELECTED]: [],
      [FACULTY_DAYS]: [],
      [NO_FACULTY_DAYS]: "",
      [EDUCATOR_TYPE]: "",
      [MAINTENANCE]: "",
      [SKU]: "",
      [NO_OF_WORKSTATIONS]: "0",
      [INCLUDES_BOOKS]: "",
      [SCHOOL_TIER]: "",
      [SYLLABUS]: [],
      [ACCOUNT_TYPE]: "",
      [STUDENT_STRENGTH]: "0"
    },
  });

  const requiredFields = getRequiredFields(schema);

  const { data: gradesRes, isPending: gradesLoading } =
    useGetCurriculumGrades();

  const gradeOptions = useMemo(() => {
    const records = gradesRes?.data || [];
    return records.map((g: any) => ({
      label: g?.curriculum_grade || g?.curriculum_name || "",
      value: g?.curriculum_id || g?.id || g?.value,
    }));
  }, [gradesRes]);

  const { data: regionsRes, isPending: regionsLoading } =
    useGetActiveGeoRegions();

  const regionOptions = useMemo(() => {
    const records = regionsRes?.data || [];
    return records.map((r: any) => ({
      label: r?.name || r?.region || "",
      value: r?.id || r?.uuid || r?.value,
    }));
  }, [regionsRes]);

  const { mutate: createSchool, isPending } = useCreateSchoolMutation({
    onSuccess: (data) => {
      // Store school ID in Redux store when basic step is completed
      const schoolId = data?.data?.schoolId || null;
      if (schoolId) {
        dispatch(setSchoolOnboardingId(schoolId));
      }
      onNextClick();
    },
  });

  const { mutate: updateSchool, isPending: isUpdating } =
    useUpdateSchoolMutation({
      onSuccess: onNextClick,
    });

  // Edit mode: fetch and prefill existing school details
  const isEdit = action === "edit" && !!id;
  const { data: schoolDetailsResponse } = useGetSchoolByIdQuery(id || "", {
    enabled: isEdit,
  } as any);

  useEffect(() => {
    const raw = (schoolDetailsResponse as any)?.data || {};
    if (!isEdit || !raw) return;

    const mappedDefaults: Partial<formValues> = {
      [SCHOOL_NAME]: raw?.schoolName ?? "",
      [STATE]: raw?.state ?? "",
      [CITY]: raw?.city ?? "",
      [ZIP]: raw?.zip ?? "",
      [REGION]: raw?.geoRegionId ?? raw?.geographical_region_id ?? "",
      [PHONE]: raw?.phoneNumber ?? raw?.phone ?? "",
      [EMAIL]: raw?.emailId ?? raw?.email ?? "",
      [WEBSITE]: raw?.website ?? "",
      [SCHOOL_LOGO]: null,
      [ADDRESS1]: raw?.address1 ?? "",
      [ADDRESS2]: raw?.address2 ?? "",

      [YEARS_OF_COLLABORATION]: String(
        raw?.academicCollaboration || raw?.academicCollaboration || ""
      ),
      [GRADES_SELECTED]: (raw?.gradesSelected as string[]) ?? [],
      [FACULTY_DAYS]: (raw?.facultyDays as string[]) ?? [],
      [NO_FACULTY_DAYS]: String(raw?.noOfFacultyDays ?? ""),
      [EDUCATOR_TYPE]: raw?.educatorType ?? "",
      [MAINTENANCE]: raw?.maintenance ?? "",
      [SKU]: raw?.sku ?? "",
      [NO_OF_WORKSTATIONS]: String(
        raw?.noOfWorkStations ?? raw?.noOfWorkstations ?? "0"
      ),
      [INCLUDES_BOOKS]: raw?.includesBooks ?? "",
      [SCHOOL_TIER]: raw?.schoolTier ?? "",
      [SYLLABUS]: (raw?.syllabus as string[]) ?? [],
      [ACCOUNT_TYPE]: raw?.accountType ?? "",
      [STUDENT_STRENGTH]: String(
        raw?.studentStrength ?? raw?.studentStrength ?? "0"
      ),
    } as any;

    form.reset({ ...(form.getValues() as any), ...(mappedDefaults as any) });
    initialValuesRef.current = mappedDefaults as Partial<formValues>;
  }, [isEdit, schoolDetailsResponse]);

  // Auto-compute noOfFacultyDays from selected Faculty Days  
useEffect(() => {
  const subscription = form.watch((value, { name }) => {
    if (name === FACULTY_DAYS) {
      const days = value?.[FACULTY_DAYS] as string[] | undefined;

      // If no days selected, set NO_FACULTY_DAYS to ""
      if (!days || days.length === 0) {
        form.setValue(NO_FACULTY_DAYS, "");
      } else {
        // Otherwise set to the count
        form.setValue(NO_FACULTY_DAYS, String(days.length));
      }
    }
  });
  return () => subscription.unsubscribe();
}, [form]);


const onSubmit = (data: formValues) => {
   
    const normalize = (s: any) =>
      (String(s || "") as string).toLowerCase().replace(/[^a-z0-9]/g, "");
    const isNonEducator = normalize(data[EDUCATOR_TYPE]) === "noneducator";


    const preparePayload = (payload: Record<string, any>) => {
      const copy = { ...(payload || {}) } as Record<string, any>;
      if (isNonEducator) {
        delete copy[FACULTY_DAYS];
        delete copy[NO_FACULTY_DAYS];
        if (copy.hasOwnProperty(FACULTY_DAYS) && Array.isArray(copy[FACULTY_DAYS]) && copy[FACULTY_DAYS].length === 0) {
          delete copy[FACULTY_DAYS];
        }
      }
      return copy;
    };

    if (isEdit) {
      const prev = initialValuesRef.current || {};

      const isValueEqual = (a: any, b: any) => {
        if (Array.isArray(a) || Array.isArray(b)) {
          return JSON.stringify(a ?? []) === JSON.stringify(b ?? []);
        }
        return (a ?? "") === (b ?? "");
      };

      const changedEntries = Object.entries(data).filter(([key, value]) => {
        const prevValue = (prev as any)[key];
        return !isValueEqual(value, prevValue);
      });

      const diffObject = changedEntries.reduce((acc, [k, v]) => {
        (acc as any)[k] = v;
        return acc;
      }, {} as Record<string, any>);

      const prepared = preparePayload(diffObject as Record<string, any>);
      const withNulls = setEmptyFieldsToNull(prepared as any);

      updateSchool({ schoolId: String(id), ...(withNulls as any) } as any);
      return;
    }

    const payloadForCreate = preparePayload(data as any);
    let cleaned = removeEmptyFields(payloadForCreate as any);
    cleaned = setEmptyFieldsToNull(cleaned);
    createSchool(cleaned as any);
  };

  const onCancel = () => {
    const userRole = loggedInUserData?.role || [];
    navigate(getSchoolRedirectPath(userRole));
  };

  return {
    form,
    requiredFields,
    onSubmit,
    onCancel,
    attachmentRef,
    regionOptions,
    gradeOptions,
    gradesLoading,
    regionsLoading,
    isSubmitting: isPending || isUpdating,
  };
};

export default useSchoolInfo;
